// C11 — Notification bell + dropdown.
//
// Header surface: a bell button with an unread-count badge ("99+" past
// the cap), and a click-popover that lists the most recent notifications.
//
// Polling: 60s interval, paused while the tab is hidden (document.
// visibilityState). The /unread-count endpoint is capped at 100 docs
// server-side so this is cheap to call.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2 } from 'lucide-react';
import NotificationItem from '../shared/NotificationItem';
import { notificationsApi } from '../../lib/notificationsApi';

const POLL_MS = 60 * 1000;
const DROPDOWN_LIMIT = 10;

export default function NotificationBell({ disabled = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState({ count: 0, is_capped: false });
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const popoverRef = useRef(null);
  const btnRef = useRef(null);
  const pollTimerRef = useRef(null);

  // ── Unread count polling ──────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const data = await notificationsApi.unreadCount();
      setUnread(data || { count: 0, is_capped: false });
    } catch {
      // Silent — surfaced when the user opens the dropdown.
    }
  }, []);

  useEffect(() => {
    if (disabled) return;
    fetchCount();
    const start = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') fetchCount();
      }, POLL_MS);
    };
    const stop = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    start();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchCount();
        start();
      } else {
        stop();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [disabled, fetchCount]);

  // ── Dropdown content ──────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const data = await notificationsApi.list({ limit: DROPDOWN_LIMIT });
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch (err) {
      setError(err.message || 'Could not load notifications.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) fetchList();
      return next;
    });
  }, [fetchList]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // ── Item actions ──────────────────────────────────────────────────────
  const handleOpen = useCallback(async (notif) => {
    // Optimistic mark-read so the badge updates immediately.
    if (!notif.read) {
      setItems((rows) =>
        rows.map((r) => r.id === notif.id ? { ...r, read: true } : r));
      setUnread((u) => ({ ...u, count: Math.max(0, u.count - 1) }));
      notificationsApi.markRead(notif.id).catch(() => {/* server-truth wins next refresh */});
    }
    if (notif.action_url) {
      setOpen(false);
      navigate(notif.action_url);
    }
  }, [navigate]);

  const handleMarkAllRead = useCallback(async () => {
    setItems((rows) => rows.map((r) => ({ ...r, read: true })));
    setUnread({ count: 0, is_capped: false });
    try {
      await notificationsApi.markAllRead();
    } catch {
      // re-fetch so we don't lie about state on failure.
      fetchCount();
    }
  }, [fetchCount]);

  const displayCount = unread.is_capped ? '99+' : String(unread.count || 0);
  const hasUnread = (unread.count || 0) > 0;

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {hasUnread && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1
                       bg-red-500 text-white text-[10px] leading-[18px]
                       text-center font-semibold rounded-full ring-2 ring-white"
            data-testid="notification-badge"
          >
            {displayCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[calc(100vw-2rem)]
                     bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {hasUnread && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-auto">
            {loadingList && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}
            {error && !loadingList && (
              <p className="text-xs text-red-600 px-4 py-6" data-testid="notification-error">
                {error}
              </p>
            )}
            {!loadingList && !error && items.length === 0 && (
              <EmptyState />
            )}
            {!loadingList && !error && items.length > 0 && (
              <ul className="divide-y divide-gray-100" data-testid="notification-list">
                {items.map((notif) => (
                  <NotificationItem
                    key={notif.id}
                    notif={notif}
                    onClickOpen={handleOpen}
                    compact
                  />
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            className="block w-full text-center px-4 py-2.5 text-xs font-medium
                       text-indigo-600 hover:bg-indigo-50 border-t border-gray-100"
            data-testid="view-all-notifications-btn"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-10 px-6" data-testid="notification-empty">
      <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
      <p className="text-sm text-gray-500">You're all caught up</p>
      <p className="text-xs text-gray-400 mt-1">
        We'll notify you when something happens.
      </p>
    </div>
  );
}
